'use client';

import {useState, useEffect} from 'react';
import { useComponentLogger } from '@/lib/component-logger';
import { htmlToTypography } from '@/lib/dataHandlers/ComponentHelpers';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, fetchAPIData } from '@/lib/dataHandlers/DataFetchers';
import { Link } from 'react-router-dom';

import { textColors, chartColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X';
import { XCircle as XCircleIcon } from '@phosphor-icons/react/dist/ssr/XCircle';
import { Question as QuestionIcon } from '@phosphor-icons/react/dist/ssr/Question';
import { icons } from '@/components/dashboard/layout/nav-icons';
import { InfoTooltip } from '../typography/info-tooltip';

/**
 * Displays a an image
 * @param {} param0 
 * @returns 
 */
export function OverlayDialog({ config = {}, notifyParentOnOpen }) {
    const logger = useComponentLogger('Overlay Dialog');
    const [isOpen, setIsOpen] = useState(false);

    logger.debug('overlay dialog config', config)

    const handleClickOpen = () => {
        if (config.tooltip) {
            notifyParentOnOpen(); // TODO: if each overlay needs to fetch its own data in the future, modify this
            setIsOpen(true);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };


    const fetchContent = async () => {
        // fetch img and overlays

        // each gen function fetches their own data?
    }

    const generateOverlay = () => {
        if (!config.type) return <></>;
        switch (config.type) {
            case "text":
                return generateText();
            case "icon":
                return generateIconButton();
            case "rectangle":
                return generateArea();
            case "ellipse":
                return generateArea();
            case "gif":
                return generateImg();
            default:
                return <></>

        }
    }

    const generateIconButton = () => {
        // validate that we have all required attributes
        if (!config.x || !config.y || !config.icon) return <></>;

        let Icon = config.icon && icons[config.icon] ? icons[config.icon] : null;
        let iconColor = config.color ? (chartColors[config.color] || config.color) : 'text.primary';
        let bgColor = config.bgColor ? (chartColors[config.bgColor] || config.bgColor) : 'transparent';
        // const iconSize = config.icon.w ? `${config.icon.w}%` : "clamp(1.25rem, 2vw, 2rem)";
        // const iconSize = config.icon.size ? `clamp(1rem, ${config.icon.size}vw, 5rem)` : "clamp(1.25rem, 2vw, 2rem)";
        const configSize = config.w || config.h || null;
        // const iconSizeMD = configSize ? `clamp(1.25rem, ${configSize}%, 10rem)` : "clamp(1.25rem, 2.5vw, 10rem)";
        // const iconSizeSM = configSize ? `clamp(1.25rem, ${configSize}%, 2rem)` : "clamp(1.25rem, 2.5vw, 2rem)";
        // const iconSize = configSize ? `clamp(1.25rem, ${configSize}%, 2rem)` : "clamp(1.25rem, 2.5vw, 2rem)";

        // const iconSize = "clamp(1.5rem, 2vw, 20rem)"; // most recent
        const iconSize = "clamp(1.5rem, 3vw, 20rem)"; // most recent

        // const iconSize = "clamp(1.25rem, 2.5vw, 2rem)";
        logger.debug('icon size', iconSize, config)

        const shouldHover = config.link || config.tooltip?.table?.rows?.length > 0;


        let link = null;
        if (config.link) {
            if (typeof config.link === "string") {
                link = config.link;
            } else if (typeof config.link === "object") link = config.link.val;
        }

        return Icon ?
                <IconButton
                    // size='small'
                    // size={iconSize}
                    // width= {{sm: iconSizeSM, md: iconSizeMD}}
                    // height= {{sm: iconSizeSM, md: iconSizeMD}}
                    sx={{
                        position: 'absolute', // place at custom coords
                        top: `${config.y.val}%`,
                        left: `${config.x.val}%`,
                        transform: 'translate(-50%, -50%)', // center the btn on the coordinate
                        // fontSize: 'clamp(1rem, 2.5vw, 2rem)',
                        color: iconColor,
                        backgroundColor: bgColor,
                        '&:hover': {
                            backgroundColor: config.link || config.tooltip ? bgColor : bgColor, // MUI applies a hover bg color, so we have to override it in addition to the opacity
                            opacity: shouldHover ? '75%' : '100%'
                        }, 
                        borderRadius: '50%',
                        aspectRatio: '1 / 1',
                        // border: '3px solid black',
                        // shrink btn around the icon
                        // padding: '0.25rem',
                        padding: '0.25vw',
                        margin: 0,
                        // width: {sm: '2rem', md: iconSize},
                        // height: {sm: '2rem', md: iconSize},
                        width: iconSize,
                        height: iconSize
                        // width: 'auto',
                        // height: 'auto'
                    }}
                    onClick={handleClickOpen}
                >
                    {config.link && !config.tooltip ? // links and tooltips are mutually exclusive -> prioritize the tooltip if both are provided
                        <Link to={link} style={{textDecoration: 'none', color: 'inherit'}} >
                            <Icon size={iconSize} /> 
                            {/* <Icon />  */}
                        </Link> 
                    : <Icon size={iconSize}/>}
                    {/*: <Icon />} */}
            </IconButton>
            : <></>
    }

    /**
     * Supports: rectangle, ellipse, square, circle
     * Draws a square/circle if height == width
     * @returns 
     */
    const generateArea = () => {
        if (config.apiExpr) {
            // fetch and set config
        } 
 
        // validate that we have all required attributes
        if (!config.x || !config.y) return <></>;

        let bgColor = chartColors[config.color] || config.color;
        let paddingBottom = 0 ; // css trick to make a circle/square widht % widths
        let width = 0;
        let height = 0;

        const shouldHover = config.link || config.tooltip?.table?.rows?.length > 0;

        // if either the height or width is missing, then render the area as a square/circle
        if (config.h && config.w) {
            width = config.w.val;
            height = config.h.val;
        } else if (config.w) {
            paddingBottom = config.w.val;
            width = config.w.val;
            height = config.w.val;
        } else if (config.h) {
            paddingBottom = config.h.val;
            width = config.h.val;
            height = config.h.val;
        }

        let Rectangle = <Box 
            width={`${width}%`} 
            height={`${height}%`} 
            minWidth={'1.25rem'}
            minHeight={'1.25rem'}
            borderRadius={config.type === 'ellipse' ? '50%' : 0}
            paddingBottom={`${paddingBottom}%`}
            bgcolor={bgColor} 
            sx={{
                position: 'absolute', // place at custom coords
                top: `${config.y.val}%`,
                left: `${config.x.val}%`,
                transform: 'translate(-50%, -50%)', // center the area on the coordinate
                '&:hover': { opacity: shouldHover ? '75%' : '100%'},
            }}
            onClick={handleClickOpen}
        />

        let link = null;
        if (config.link) {
            if (typeof config.link === "string") {
                link = config.link;
            } else if (typeof config.link === "object") link = config.link.val;
        }
        
        return config.link && !config.tooltip ? // links and tooltips are mutually exclusive -> prioritize the tooltip if both are provided
                <Link to={link} style={{textDecoration: 'none', color: 'inherit'}} >
                    { Rectangle }
                </Link> 
                : Rectangle
                    
    }

    const generateText = () => {
        let val = null;
        if (typeof config.val === 'object') {
            if (config.val.apiExpr) {
                // fetch data
            } else if (config.val.val) {
                val = config.val.unit ? `${config.val.val}${config.val.unit}` : config.val.val;
            }
        } else val = config.val

        logger.debug('text config', config, val, config.bgColor);

        // validate that we have all required attributes
        if (!config.x || !config.y || !val) return <></>;

        // const fontSize = "clamp(1rem, 2.5vw, 100rem)";
        const fontSize = "clamp(0.75rem, 1vw, 10rem)";
        const fontObject = {
            xs: "clamp(0.75rem, 1vw, 10rem)",
            sm: "clamp(0.75rem, 1vw, 10rem)",
            lg: "clamp(0.75rem, 1vw, 10rem)"
        }
        const textColor = config.color ? (textColors[config.color] || config.color) : 'text.primary';

        // if bgColor is provided, then make the btn a circle
        const bgColor = config.bgColor ? (chartColors[config.bgColor] || config.bgColor) : 'transparent';
        const bgShape = config.bgShape ?? 'rect';
        const borderRadius = bgShape === 'circ' ? '50%' : '';
        const aspectRatio = bgShape === 'circ' ? '1 / 1' : '';
        // const border = config.bgColor ? `2px solid ${textColor}` : '';
        const hasBorder = Boolean(config.bgColor);

        let link = null;
        if (config.link) {
            if (typeof config.link === "string") {
                link = config.link;
            } else if (typeof config.link === "object") link = config.link?.val;
        }

        const shouldHover = config.link || config.tooltip?.table?.rows?.length > 0;

        return val ? 
                <Button 
                    onClick={handleClickOpen}
                    sx={{
                        position: 'absolute', // place at custom coords
                        top: `${config.y.val}%`,
                        left: `${config.x.val}%`,
                        transform: 'translate(-50%, -50%)', // center the btn on the coordinate
                        backgroundColor: bgColor,
                        '&:hover': {
                            backgroundColor: shouldHover ? bgColor : bgColor, // MUI applies a hover bg color, so we have to override it in addition to the opacity
                            opacity: shouldHover ? '75%' : '100%'
                        }, 
                        borderRadius: borderRadius,
                        aspectRatio: aspectRatio,
                        // border: (theme) => `2px solid ${theme.palette.text.primary}`,
                        border: (theme) => {
                            if (!hasBorder) return '';

                            // Resolve theme keys (text.primary, primary.main, etc.)
                            const resolved =
                            textColor.includes('.')
                                ? textColor.split('.').reduce((obj, key) => obj?.[key], theme.palette)
                                : textColor;

                            const borderObj = {
                                xs: ''
                            }

                            // return `2px solid ${resolved}`;
                            return `0.1rem solid ${resolved}`;
                        },
                        // shrink btn around the text
                        padding: '0.1rem 0.25rem',
                        margin: 0,
                        width: 'auto',
                        maxWidth: '25vw' // to allow absolutely positioned text to wrap
                        // height: 'auto',
                        // fontSize: "clamp(1.25rem, 0.25vw, 2rem)",
                    }}
                >
                    {config.link && !config.tooltip ? // links and tooltips are mutually exclusive -> prioritize the tooltip if both are provided
                        <Link to={link} style={{textDecoration: 'none', color: 'inherit'}} >
                            <Typography variant='button' color={textColor} fontSize={fontSize} textTransform={'none'} textAlign={'left'}>{htmlToTypography(val)}</Typography> 
                        </Link> 
                        : <Typography variant='button' color={textColor} fontSize={fontSize} textTransform={'none'} textAlign={'left'}>{htmlToTypography(val)}</Typography>
                    }
                </Button>
                : <></>
    }

    /**
     * Renders gifs, pngs, jpgs
     * @returns 
     */
    const generateImg = () => {
        if (config.apiExpr) {
            // fetch and set config
        } 
        // validate that we have all required attributes
        if (!config.w || !config.h || !config.x || !config.y || !config.gif) return <></>;

        let paddingBottom =  config.h.val === config.w.val ? config.h.val : 0; // css trick to make a circle/square widht % widths

        let imgSrc = null;
        if (typeof config.gif === "string") {
            imgSrc = config.gif;
        } else if (typeof config.gif === "object") imgSrc = config.gif.val;

        const shouldHover = config.link || config.tooltip?.table?.rows?.length > 0;
        

        let Rectangle = <Box 
            width={`${config.w.val}%`} 
            // height={`${config.h.val}%`} // have to omit either height or paddingBottom in order for gif to show
            borderRadius={config.type === 'ellipse' ? '50%' : 0}
            paddingBottom={`${paddingBottom}%`}
            sx={{
                position: 'absolute', // place at custom coords
                top: `${config.y.val}%`,
                left: `${config.x.val}%`,
                transform: 'translate(-50%, -50%)', // center the area on the coordinate
                '&:hover': { opacity: shouldHover ? '75%' : '100%' },
            }}
            onClick={handleClickOpen}
        >
            <Box
                component="img"
                src={imgSrc}
                alt="GIF"
                sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                }}
            />
        </Box>

        let link = null;
        if (config.link) {
            if (typeof config.link === "string") {
                link = config.link;
            } else if (typeof config.link === "object") link = config.link.val;
        }

        logger.debug('gif done', config);
        
        return config.link && !config.tooltip ? // links and tooltips are mutually exclusive -> prioritize the tooltip if both are provided
                <Link to={link} style={{textDecoration: 'none', color: 'inherit'}} >
                    { Rectangle }
                </Link> 
                : Rectangle
                    
    }


    /**
     * Rows consist of:
     * 1. Optional icon prefix (either a text with the name or an obj with name and color)
     * 2. Optional bold prefix
     * 3. Optional link wrapped around the bold prefix
     * 4. Text
     * @returns 
     */
    const generateDialogRows = () => {
        logger.debug('dialog rows', Array.isArray(config.tooltip), config.tooltip);
        if (!config.tooltip) return <></>;
        let tableRows = null;
        if (config.tooltip.table?.rows) {
            tableRows = config.tooltip.table.rows;
        } else if (Array.isArray(config.tooltip.table)) {
            tableRows = config.tooltip.table;
        }
        if (!tableRows) return <></>;
        

        return tableRows.map((item, index) => {
            // logger.debug('row item', item);
            let RowIcon = item.icon && icons[item.icon] ? icons[item.icon] : null;
            let iconColor = item.iconColor ? (chartColors[item.iconColor] || item.iconColor) : 'text.primary';
            let textColor = item.color ? (textColors[item.color] || item.color) : 'text.primary';
            let itemValue = '';
            if (item.hasOwnProperty('val')) {
                if (typeof item.val === 'object') {
                    itemValue = item.val.unit ? `${item.val.val}${item.val.unit}` : item.val.val;
                } else itemValue = `${item.val}`
            }

            let RowText = <Typography variant="body1" component="span">
                            {item.name ? 
                                <Typography variant='subtitle1' component="span" >
                                        {`${item.name}: `}
                                </Typography>
                            : <></>}
                                <Typography variant='body1' component="span" color={textColor}>
                                        {itemValue}
                                </Typography>
                            </Typography>

            let link = null;
            if (item.link) {
                if (typeof item.link === "string") {
                    link = item.link;
                } else if (typeof item.link === "object"){
                    link = item.link.val;
                } 
            }


            return <Stack key={index} direction="row" alignItems="flex-start" spacing={1}>
                {RowIcon ? 
                    <Box sx={{ display: 'flex', alignItems: 'center', pt: '2px', }}>
                        <RowIcon size={20} weight='fill' color={iconColor}/>
                    </Box>
                : <></>}
                {item.link ? 
                    <Link to={link} style={{textDecoration: 'none', color: 'inherit'}}>
                        { RowText }
                    </Link>
                : RowText}
            </Stack>

        });
    }


    return (
        <>
        {generateOverlay()}
        {/* { config.tooltip && config.tooltip.table && Object.entries(config.tooltip.table).length > 0 ?  */}
        { config.tooltip && config.tooltip.table?.rows?.length > 0 ? 
            <Dialog
                onClose={handleClose}
                open={isOpen}
            >
                { config.tooltip?.title ? 
                    <Stack direction='row' sx={{display: 'flex', justifyContent: 'center'}}>
                        { config.tooltip.title ? 
                            <DialogTitle sx={{ m: 0, p: 2 }}>
                                {config.tooltip.titleLink ? 
                                    <Link to={config.tooltip.titleLink.val || config.tooltip.titleLink} style={{textDecoration: 'none', color: 'inherit'}}> {config.tooltip.title} </Link>
                                : config.tooltip.title
                                }
                            </DialogTitle>
                        : <></>}
                        <IconButton
                            onClick={handleClose}
                            sx={{
                                ml: 'auto',
                            }}
                        >
                            <XIcon />
                        </IconButton>
                    </Stack>
                : <></> }
                <DialogContent dividers>
                    {generateDialogRows()}
                </DialogContent>
                {/* <DialogActions>
                    <Button autoFocus onClick={handleClose}>
                        Save changes
                    </Button>
                </DialogActions> */}
            </Dialog>
        : <></> }
        </>
    );
}